import { create } from 'zustand';
import { api, get, refreshAccessToken, send, setAccessToken, setSessionLostHandler } from '../api/client';
import type { Profile } from '../api/types';
import { queryClient } from '../api/queryClient';

type Status = 'loading' | 'authed' | 'anon';

interface AuthState {
  status: Status;
  user: Profile | null;
  /** true when the session ended on its own (idle or revoked) rather than by the user */
  expired: boolean;
  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<Profile>;
  logout: (opts?: { expired?: boolean }) => Promise<void>;
  reload: () => Promise<void>;
  updateProfile: (input: { name: string; title: string | null }) => Promise<void>;
  uploadPhoto: (file: File) => Promise<void>;
  removePhoto: () => Promise<void>;
  can: (...codes: string[]) => boolean;
}

/** Own-profile calls answer with the refreshed profile, which replaces the stored one. */
const own = (r: Profile) => ({ user: r });

export const useAuth = create<AuthState>((set, getState) => ({
  status: 'loading',
  user: null,
  expired: false,

  /** On load, try to restore the session from the refresh cookie (no password re-entry). */
  async bootstrap() {
    const token = await refreshAccessToken();
    if (!token) return set({ status: 'anon', user: null });
    try {
      const user = await get<Profile>('/auth/me');
      set({ status: 'authed', user, expired: false });
    } catch {
      setAccessToken(null);
      set({ status: 'anon', user: null });
    }
  },

  async login(email, password) {
    const r = await api.post('/auth/login', { email, password });
    setAccessToken(r.data.data.accessToken);
    const user = r.data.data.user as Profile;
    queryClient.clear();
    set({ status: 'authed', user, expired: false });
    return user;
  },

  async logout(opts) {
    try {
      await api.post('/auth/logout', {}, { headers: { 'X-Requested-With': 'fms-web' } });
    } catch {
      /* the cookie may already be gone */
    }
    setAccessToken(null);
    queryClient.clear();
    set({ status: 'anon', user: null, expired: !!opts?.expired });
  },

  async reload() {
    const user = await get<Profile>('/auth/me');
    set({ user });
  },

  async updateProfile(input) {
    set(own(await send<Profile>('patch', '/auth/me', input)));
  },

  async uploadPhoto(file) {
    set(own((await api.put('/auth/me/photo', file, { headers: { 'Content-Type': file.type } })).data.data as Profile));
  },

  async removePhoto() {
    set(own(await send<Profile>('delete', '/auth/me/photo')));
  },

  can: (...codes) => {
    const u = getState().user;
    return !!u && codes.some((c) => u.permissions.includes(c));
  },
}));

setSessionLostHandler(() => void useAuth.getState().logout({ expired: true }));

export const useCan = () => {
  const user = useAuth((s) => s.user);
  return (...codes: string[]) => !!user && codes.some((c) => user.permissions.includes(c));
};

export const changePasswordCall = (currentPassword: string, newPassword: string) =>
  send('post', '/auth/change-password', { currentPassword, newPassword });
